import { useState } from 'react';
import { useFormik } from 'formik';
import {
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Container,
  Card,
} from '@mui/material';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import { useAppDispatch } from '../store/store';
import { login as auth } from '../store/slices/auth';
import forgotPasswordService from '../api/services/forgotPassword';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState<string | null>(
    searchParams.get('message')
  );
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const location = useLocation();
  const from = location.state?.from;
  const redirectTo = from
    ? `${from.pathname ?? from}${from.search ?? ''}`
    : '/deals';

  const handleForgotPassword = async () => {
    try {
      await forgotPasswordService.patch({ email: resetEmail });
    } finally {
      setMessage('If that email has an account, a reset link is on its way.');
      setForgotPasswordOpen(false);
    }
  };

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    onSubmit: async (values) => {
      setLoading(true);
      setError(false);
      try {
        const res = await dispatch(
          auth({ email: values.email, password: values.password })
        );
        if (
          res.meta.requestStatus === 'fulfilled' &&
          (res.payload as any)?.id
        ) {
          navigate(redirectTo, { replace: true });
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      }
      setLoading(false);
    },
  });

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ bgcolor: '#f6f9fb' }}
    >
      <Container maxWidth="xs">
        <Card sx={{ p: 4 }}>
          <Stack direction="column" spacing={2}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Typography variant="h5" fontWeight="bold" color="primary">
                DealerScout
              </Typography>
            </Link>
            <Typography variant="h4" sx={{ fontWeight: 700 }} fontSize={32}>
              Welcome back
            </Typography>
            <form onSubmit={formik.handleSubmit}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                type="email"
                required
                sx={{ mb: 2 }}
                placeholder="hi@example.com"
                {...formik.getFieldProps('email')}
              />
              <TextField
                label="Password"
                variant="outlined"
                fullWidth
                required
                type="password"
                {...formik.getFieldProps('password')}
              />
              {error && (
                <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                  Unable to log in. Please double-check your email and password.
                </Alert>
              )}
              {message && (
                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  {decodeURIComponent(message)}
                </Alert>
              )}
              <Button
                variant="text"
                fullWidth
                color="secondary"
                sx={{ mb: 1, mt: 1 }}
                onClick={() => {
                  setResetEmail(formik.values.email);
                  setForgotPasswordOpen(true);
                }}
              >
                Forgot Password?
              </Button>
              <LoadingButton
                variant="contained"
                sx={{ mb: 2 }}
                fullWidth
                type="submit"
                loading={loading}
              >
                Log In
              </LoadingButton>
            </form>
            <Typography variant="subtitle2" align="center">
              Don&apos;t have an account?{' '}
              <Link to="/signup" state={from ? { from } : undefined}>
                Sign up for free
              </Link>
            </Typography>
          </Stack>
        </Card>
      </Container>

      <Dialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
      >
        <DialogTitle>Forgot Password</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Enter your email to receive a reset link.
          </Typography>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            type="email"
            required
            autoFocus
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            sx={{ mt: 0.5, minWidth: 300 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotPasswordOpen(false)}>Cancel</Button>
          <Button onClick={handleForgotPassword} disabled={!resetEmail}>
            Send Reset Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
